package br.com.vvrefeicoes.portal.feature.purchases

import br.com.vvrefeicoes.portal.core.util.spreadsheet.SpreadsheetFormatException
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraImportOrder
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

data class PurchasesExtraUnitGroup(
    val label: String,
    val count: Int,
)

data class PurchasesExtraParseResult(
    val orders: List<PurchasesExtraImportOrder>,
    val unitGroups: List<PurchasesExtraUnitGroup>,
)

/**
 * Lê a planilha de pedidos extras: cabeçalho na linha 5, A = quantidade, B = data do pedido,
 * C = unidade + descrição e D = data de entrega. O texto completo de C vira a descrição.
 */
internal fun parsePurchasesExtraOrders(
    rows: List<List<String>>,
): PurchasesExtraParseResult {
    val header = rows.getOrNull(4) ?: throw SpreadsheetFormatException("Cabeçalho da planilha não encontrado")
    if (header.size < 4) {
        throw SpreadsheetFormatException("A planilha de pedidos extras não tem as colunas esperadas")
    }

    val orders = mutableListOf<PurchasesExtraImportOrder>()
    val groups = linkedMapOf<String, PurchasesExtraUnitGroup>()
    rows.drop(5).forEach { row ->
        val quantityText = row.getOrNull(0)?.trim().orEmpty()
        val orderDateText = row.getOrNull(1)?.trim().orEmpty()
        val unitAndDescription = row.getOrNull(2)?.trim().orEmpty()
        val deliveryDateText = row.getOrNull(3)?.trim().orEmpty()
        if (quantityText.isEmpty() && orderDateText.isEmpty() && unitAndDescription.isEmpty() && deliveryDateText.isEmpty()) {
            return@forEach
        }

        val orderDate = parseExtraDate(orderDateText) ?: return@forEach
        val sourceLabel = extraUnitLabel(unitAndDescription)
        if (sourceLabel != null) {
            val key = normalizeExtraUnitLabel(sourceLabel)
            val current = groups[key]
            groups[key] =
                PurchasesExtraUnitGroup(
                    label = current?.label ?: sourceLabel,
                    count = (current?.count ?: 0) + 1,
                )
        }
        orders +=
            PurchasesExtraImportOrder(
                orderDate = orderDate.toString(),
                deliveryDate = parseExtraDate(deliveryDateText)?.toString(),
                quantity = parseExtraQuantity(quantityText),
                description = unitAndDescription,
                sourceUnitLabel = sourceLabel,
            )
    }

    if (orders.isEmpty()) {
        throw SpreadsheetFormatException("Não há pedidos na planilha.")
    }
    return PurchasesExtraParseResult(orders = orders, unitGroups = groups.values.toList())
}

internal fun extraUnitLabel(description: String): String? {
    val text = description.trim()
    if (text.isEmpty()) return null
    val prefix = text.split(Regex("\\s*[-–—]\\s*|\\s*\\("), limit = 2).first().trim()
    return prefix.ifEmpty { text }
}

internal fun normalizeExtraUnitLabel(value: String): String =
    value
        .trim()
        .uppercase()
        .replace("Á", "A")
        .replace("À", "A")
        .replace("Ã", "A")
        .replace("Â", "A")
        .replace("É", "E")
        .replace("Ê", "E")
        .replace("Í", "I")
        .replace("Ó", "O")
        .replace("Ô", "O")
        .replace("Õ", "O")
        .replace("Ú", "U")
        .replace("Ç", "C")
        .replace(Regex("[^A-Z0-9]+"), " ")
        .trim()

private fun parseExtraQuantity(value: String): Int? {
    if (value.isBlank()) return null
    val trimmed = value.trim()
    return trimmed.toIntOrNull() ?: trimmed.toDoubleOrNull()?.toInt()
}

private fun parseExtraDate(value: String): LocalDate? {
    if (value.isBlank()) return null
    val trimmed = value.trim()
    val parts = trimmed.split("/")
    if (parts.size == 3) {
        val day = parts[0].toIntOrNull()
        val month = parts[1].toIntOrNull()
        val year = parts[2].toIntOrNull()
        if (day != null && month != null && year != null) {
            return runCatching { LocalDate(year, month, day) }.getOrNull()
        }
    }
    return trimmed
        .toDoubleOrNull()
        ?.takeIf { it > 0 }
        ?.toInt()
        ?.let { LocalDate(1899, 12, 30).plus(it, DateTimeUnit.DAY) }
        ?: runCatching { LocalDate.parse(trimmed) }.getOrNull()
}
